// THE single source of truth for "which file types may a customer upload, and
// where." Consumed by BOTH the SPA fast-feedback guard (useLineAssets
// .uploadRejectionReason + UploadSlot's file-picker `accept`) and the
// AUTHORITATIVE backend gate (storage/service.requestUploadUrl +
// storage/assets.registerAsset), so the two can't drift — the same "one shared
// gate" convention lineConfigIssues / validateRoleSet follow.
//
// The accept policy is keyed on the component_role's TIER, not one global list:
//   - manuscript ('guts'): the interior pages must be a fixed-layout PDF (a
//     .docx repaginates per machine/font, so it carries no fixed print layout).
//     PDF only — deliberately strict, unchanged.
//   - catch_all ('combined' — the "just give us all the files, we'll work with
//     you" Hands-on Prep area): ANY file type. A partner who only has a Word
//     doc, an InDesign package, a spreadsheet, or fonts drops them here and our
//     prepress team parses / preps / converts by hand (e.g. Word → PDF).
//   - data ('variable_data', 'variable_data_list'): the personalization /
//     mail-merge list — the names / addresses / serials that change per piece.
//     This is DATA, not artwork, so it accepts the tabular + text formats we
//     actually receive (CSV, tab-separated, plain text, Markdown, Excel, and
//     other spreadsheet exports) — NOT the image/design allow-list. This was the
//     bug the help text ("A spreadsheet works best") contradicted: the slot said
//     spreadsheet but the picker filtered to images only.
//   - photo ('assembly_kit_photo'): a reference snapshot of the finished kit
//     as a whole — or, on a rework, of the stock — taken on a phone and never
//     printed. The artwork list REJECTS the two formats a modern phone actually
//     produces — Apple writes .heic/.heif by default and Android .webp — so
//     reusing it here would refuse the single most likely upload on this slot.
//     Print formats are still accepted (a partner may photograph a proof and
//     hand us a PDF or a TIFF), and so is a spreadsheet: a partner pointing at
//     stock we already hold often has the kit's BOM sheet, not a picture.
//   - step_reference ('assembly_step_photo'): everything the photo tier takes,
//     plus the documents a partner already describes a build step with — a BOM
//     spreadsheet, a Word or PowerPoint work instruction, a Visio flow, a
//     Markdown or Mermaid diagram. Never printed, so no print-format concern.
//   - artwork (every other role): the print formats graphics can receive.
//
// FILE EXTENSION is the AUTHORITATIVE signal; MIME is a secondary accept path.
// Browsers / OS pickers report MIME unreliably for pro design formats — .psd,
// .indd, and .ai routinely arrive as application/octet-stream or '' — so a
// MIME-only allow-list silently rejects real artwork. This mirrors the
// extension-first approach the Word-doc guard already used.
// The per-file ceiling for every customer upload slot, the interior-pages PDF
// included. The SPA refuses a bigger file before sending it, the /upload route
// caps the buffered body at it, and the service re-checks the measured length;
// a larger file reaches Argo through the customer's contact, out of band. The
// API holds the whole body in memory while it hashes and writes it, so this
// number is also a per-request heap cost on the API replica.
export const MAX_UPLOAD_BYTES = 150 * 1024 * 1024;
// The same ceiling in whole MB, for customer-facing copy.
export const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / (1024 * 1024);
// Sales-confirmed artwork formats graphics can receive (.psd, .ai, .tiff, .ps,
// .jpeg, .jpg, .indd), plus the raster/vector reference + collateral formats
// already accepted (pdf, png, svg, eps, zip). Lowercase, no leading dot.
export const ARTWORK_EXTENSIONS = new Set([
    'pdf',
    'ai',
    'psd',
    'indd',
    'eps',
    'ps',
    'tif',
    'tiff',
    'jpg',
    'jpeg',
    'png',
    'svg',
    'zip',
]);
// MIME types trusted as a SECONDARY accept path for artwork (accepted even if
// the extension somehow wasn't recognized). Never the sole authority — see the
// file header on why extension wins.
export const ARTWORK_CONTENT_TYPES = new Set([
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/tiff',
    'image/svg+xml',
    'application/zip',
    'application/postscript',
    'application/illustrator',
    'image/vnd.adobe.photoshop',
]);
// Manuscript / interior pages — fixed-layout PDF only.
export const MANUSCRIPT_EXTENSIONS = new Set(['pdf']);
// Personalization / variable-data lists — the tabular + text formats a customer
// actually hands us for names / addresses / serials. Spreadsheet exports (csv,
// tsv, xls, xlsx, ods, and Apple Numbers), delimited/plain text (txt, tab, tsv),
// and Markdown tables. Lowercase, no leading dot.
export const DATA_EXTENSIONS = new Set([
    'csv',
    'tsv',
    'tab',
    'txt',
    'md',
    'markdown',
    'xls',
    'xlsx',
    'xlsm',
    'ods',
    'numbers',
]);
// MIME types trusted as a SECONDARY accept path for data files. Never the sole
// authority — extension wins (browsers report empty / octet-stream MIME for many
// spreadsheet exports).
export const DATA_CONTENT_TYPES = new Set([
    'text/csv',
    'text/tab-separated-values',
    'text/plain',
    'text/markdown',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
]);
// Reference photos of a kit build step. The phone formats first — HEIC/HEIF is
// what an iPhone writes with "High Efficiency" on (the factory default) and WEBP
// is common off Android and the web — then the ordinary web/raster formats, then
// the print formats, because a partner may just as well photograph a proof and
// hand us a PDF. Lowercase, no leading dot.
export const PHOTO_EXTENSIONS = new Set([
    'heic',
    'heif',
    'webp',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tif',
    'tiff',
    'avif',
    'pdf',
]);
// MIME types trusted as a SECONDARY accept path for photos. Never the sole
// authority — extension wins, and it matters more here than anywhere else:
// browsers routinely report '' or application/octet-stream for a HEIC picked out
// of a phone's photo library.
export const PHOTO_CONTENT_TYPES = new Set([
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence',
    'image/webp',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/avif',
    'application/pdf',
]);
// Spreadsheets — a kit's BOM sheet. Accepted on BOTH assembly photo slots: the
// finished-kit / stock slot takes these and nothing else beyond the photo set,
// the step slot takes them as part of its wider document set. Lowercase, no
// leading dot.
export const SPREADSHEET_EXTENSIONS = new Set([
    'xls',
    'xlsx',
    'xlsm',
    'csv',
    'ods',
]);
export const SPREADSHEET_CONTENT_TYPES = new Set([
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'text/csv',
    'application/vnd.oasis.opendocument.spreadsheet',
]);
// What the finished-kit / stock slot accepts: a photo, or the BOM spreadsheet.
export const KIT_PHOTO_EXTENSIONS = new Set([
    ...PHOTO_EXTENSIONS,
    ...SPREADSHEET_EXTENSIONS,
]);
export const KIT_PHOTO_CONTENT_TYPES = new Set([
    ...PHOTO_CONTENT_TYPES,
    ...SPREADSHEET_CONTENT_TYPES,
]);
// Build-step reference documents: Office (Word, Excel, PowerPoint, Visio), their
// OpenDocument siblings, plain/rich text, CSV, and Markdown / Mermaid diagram
// source. Added ON TOP of the photo set for the step slot only — the
// finished-kit slot takes only the spreadsheets. Lowercase, no leading dot.
export const STEP_DOCUMENT_EXTENSIONS = new Set([
    'doc',
    'docx',
    ...SPREADSHEET_EXTENSIONS,
    'ppt',
    'pptx',
    'vsd',
    'vsdx',
    'odt',
    'odp',
    'rtf',
    'txt',
    'md',
    'markdown',
    'mmd',
    'mermaid',
]);
export const STEP_DOCUMENT_CONTENT_TYPES = new Set([
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ...SPREADSHEET_CONTENT_TYPES,
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.visio',
    'application/vnd.ms-visio.drawing',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.presentation',
    'application/rtf',
    'text/rtf',
    'text/plain',
    'text/markdown',
]);
export const STEP_REFERENCE_EXTENSIONS = new Set([
    ...PHOTO_EXTENSIONS,
    ...STEP_DOCUMENT_EXTENSIONS,
]);
export const STEP_REFERENCE_CONTENT_TYPES = new Set([
    ...PHOTO_CONTENT_TYPES,
    ...STEP_DOCUMENT_CONTENT_TYPES,
]);
// Best-effort extension → canonical MIME, used to give an upload a meaningful
// content_type when the browser reported none — so the stored Asset row, the
// file-type pill, and the download response aren't all 'application/octet-stream'.
// Absence here means "leave whatever the browser sent"; it is NOT a rejection
// (the tier gate below decides acceptance). .indd has no registered MIME.
const EXTENSION_CONTENT_TYPE = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    svg: 'image/svg+xml',
    zip: 'application/zip',
    eps: 'application/postscript',
    ps: 'application/postscript',
    ai: 'application/illustrator',
    psd: 'image/vnd.adobe.photoshop',
    // Phone-camera formats (build-step reference photos). A browser that reports
    // nothing for a HEIC would otherwise store it as octet-stream, which then
    // reads back as an unpreviewable blob rather than a picture.
    heic: 'image/heic',
    heif: 'image/heif',
    webp: 'image/webp',
    gif: 'image/gif',
    bmp: 'image/bmp',
    avif: 'image/avif',
    // Data-file (variable-data list) formats.
    csv: 'text/csv',
    tsv: 'text/tab-separated-values',
    tab: 'text/tab-separated-values',
    txt: 'text/plain',
    md: 'text/markdown',
    markdown: 'text/markdown',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ods: 'application/vnd.oasis.opendocument.spreadsheet',
    // Build-step reference documents.
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    vsd: 'application/vnd.visio',
    vsdx: 'application/vnd.ms-visio.drawing',
    odt: 'application/vnd.oasis.opendocument.text',
    odp: 'application/vnd.oasis.opendocument.presentation',
    rtf: 'application/rtf',
};
// Word MIME types — used only to give a Word doc dropped on an ARTWORK or
// MANUSCRIPT slot a helpful "use the catch-all / convert to PDF" message. Word
// IS accepted on the catch-all slot (we convert it for the customer).
const WORD_MIME_TYPES = new Set([
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
// A lowercased, dot-less file extension, or '' when there is none.
export function fileExtensionLower(filename) {
    const dot = filename.lastIndexOf('.');
    if (dot < 0 || dot === filename.length - 1)
        return '';
    return filename.slice(dot + 1).toLowerCase();
}
function isWordDoc(filename, contentType) {
    const ext = fileExtensionLower(filename);
    return WORD_MIME_TYPES.has(contentType) || ext === 'doc' || ext === 'docx';
}
// The accept tier for a component_role. The manuscript slot is PDF-only, the
// Hands-on Prep catch-all takes anything, the variable-data roles take tabular
// data files, and everything else is an artwork slot.
export function uploadTierForRole(role) {
    if (role === 'guts')
        return 'manuscript';
    if (role === 'combined')
        return 'catch_all';
    if (role === 'variable_data' || role === 'variable_data_list')
        return 'data';
    if (role === 'assembly_step_photo')
        return 'step_reference';
    if (role === 'assembly_kit_photo')
        return 'photo';
    return 'artwork';
}
// Canonical content_type for a file: prefer the browser-reported type, fall
// back to an extension lookup, finally to octet-stream. Keeps the signed-URL
// PUT header, the stored Asset row, and the download response consistent — and
// gives .psd / .ai a real MIME even when the browser reported none.
export function normalizeUploadContentType(filename, browserType) {
    if (browserType)
        return browserType;
    // `Object.hasOwn` before the read, because `filename` is customer input and
    // the map is a plain object literal: a file named `art.constructor` otherwise
    // resolves to `Object` itself, and `art.__proto__` to an object — neither is
    // nullish, so `??` never fires and a non-string escapes a `string` return into
    // the signed-URL PUT header and the stored Asset.content_type. Only the
    // all-lowercase prototype members can reach here (the extension is lowercased
    // first), which is why it survived: `.toString` already misses.
    const ext = fileExtensionLower(filename);
    const mapped = Object.hasOwn(EXTENSION_CONTENT_TYPE, ext)
        ? EXTENSION_CONTENT_TYPE[ext]
        : undefined;
    return mapped ?? 'application/octet-stream';
}
// THE upload accept gate. Returns a customer-facing rejection message, or null
// when the file is acceptable for the given role. `content_type` may be '' /
// null (browser reported none) — the extension then decides.
export function uploadFileTypeRejection(args) {
    const tier = uploadTierForRole(args.role);
    const ext = fileExtensionLower(args.filename);
    const contentType = args.content_type ?? '';
    // The Hands-on Prep catch-all takes anything — a person preps/converts it.
    if (tier === 'catch_all')
        return null;
    if (tier === 'data') {
        if (DATA_EXTENSIONS.has(ext))
            return null;
        if (contentType && DATA_CONTENT_TYPES.has(contentType))
            return null;
        // No reliable signal at all (no extension AND no MIME): let it through — the
        // browser gave us nothing to judge on. Matches the artwork-tier posture.
        if (ext === '' && contentType === '')
            return null;
        return 'That file type isn’t an accepted data file here. Upload your list as a spreadsheet (CSV, Excel .xlsx/.xls, or .ods) or a text file (.txt, tab-separated, or Markdown). Anything else can go in the “just give us all the files” area.';
    }
    if (tier === 'photo') {
        if (KIT_PHOTO_EXTENSIONS.has(ext))
            return null;
        if (contentType && KIT_PHOTO_CONTENT_TYPES.has(contentType))
            return null;
        // No reliable signal at all: let it through, same posture as the other
        // tiers. A phone photo with a stripped extension and an empty MIME is a real
        // case, and refusing it would be a false negative on the likeliest upload.
        if (ext === '' && contentType === '')
            return null;
        return 'That doesn’t look like a photo or a spreadsheet. Snap a picture with your phone (HEIC, JPG, or PNG all work), or upload a PDF or an Excel sheet of the kit.';
    }
    if (tier === 'step_reference') {
        if (STEP_REFERENCE_EXTENSIONS.has(ext))
            return null;
        if (contentType && STEP_REFERENCE_CONTENT_TYPES.has(contentType))
            return null;
        if (ext === '' && contentType === '')
            return null;
        return 'That file type isn’t accepted for a step. Upload a photo (HEIC, JPG, or PNG), a PDF, or a document: Word, Excel, PowerPoint, Visio, text, Markdown, or Mermaid. Anything else can go in the “just give us all the files” area.';
    }
    if (tier === 'manuscript') {
        if (ext === 'pdf' || contentType === 'application/pdf')
            return null;
        if (isWordDoc(args.filename, contentType)) {
            return 'The interior pages need to be a print-ready PDF so they print exactly as you laid them out. A Word file repaginates per machine and font. In Word choose File → Save As (or Export), pick PDF, then upload the PDF. (Or drop everything in the “just give us all the files” area and we’ll convert it for you.)';
        }
        return 'The interior pages must be a print-ready PDF. Save your file as a PDF and upload that.';
    }
    // Artwork tier.
    if (ARTWORK_EXTENSIONS.has(ext))
        return null;
    if (contentType && ARTWORK_CONTENT_TYPES.has(contentType))
        return null;
    // No reliable signal at all (no extension AND no MIME): let it through — the
    // browser gave us nothing to judge on, and the backend re-checks. Matches the
    // prior "empty MIME passes rather than false-negative" posture.
    if (ext === '' && contentType === '')
        return null;
    if (isWordDoc(args.filename, contentType)) {
        return 'Word documents aren’t an artwork format. Drop it in the “just give us all the files” area instead, and we’ll convert it to a print-ready PDF for you. (Or save it as a PDF and upload that here.)';
    }
    return 'That file type isn’t an accepted artwork format here. Upload a PDF (preferred), an image (PNG / JPG / TIFF), or a design file (AI, PSD, INDD, EPS). Anything else can go in the “just give us all the files” area.';
}
// File-picker `accept` attribute per role. PRESENTATION only — the gate above
// is the authority. The catch-all uses '' (no filter → the OS shows all files),
// which is what makes the "give us all the files" area accept anything.
export const ARTWORK_ACCEPT = 'application/pdf,image/png,image/jpeg,image/tiff,image/svg+xml,application/zip,application/postscript,application/illustrator,image/vnd.adobe.photoshop,.pdf,.ai,.psd,.indd,.eps,.ps,.tif,.tiff,.jpg,.jpeg,.png,.svg,.zip';
export const MANUSCRIPT_ACCEPT = 'application/pdf,.pdf';
export const CATCH_ALL_ACCEPT = '';
export const DATA_ACCEPT = 'text/csv,text/tab-separated-values,text/plain,text/markdown,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,.csv,.tsv,.tab,.txt,.md,.markdown,.xls,.xlsx,.xlsm,.ods,.numbers';
// The photo picker's filter. image/jpeg comes FIRST and HEIC is never named, not
// even through image/*: iOS Safari's chooser then hands over a JPEG transcode of
// a HEIC photo instead of the HEIC (WebKit 292350), and no browser or renderer
// here can draw a HEIC. The gate still accepts .heic/.heif, so a HEIC that
// arrives some other way (drag-and-drop, paste, "All files") still uploads.
export const PHOTO_PICKER_OMITTED_EXTENSIONS = new Set(['heic', 'heif']);
// The camera control's filter. JPEG alone, so a camera that could write HEIC
// writes JPEG instead.
export const PHOTO_CAPTURE_ACCEPT = 'image/jpeg';
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/avif,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff,.avif,.pdf';
export const KIT_PHOTO_ACCEPT = `${PHOTO_ACCEPT},${[...SPREADSHEET_EXTENSIONS]
    .map((ext) => `.${ext}`)
    .join(',')}`;
export const STEP_REFERENCE_ACCEPT = `${PHOTO_ACCEPT},${[...STEP_DOCUMENT_EXTENSIONS]
    .map((ext) => `.${ext}`)
    .join(',')}`;
export function acceptAttrForRole(role) {
    const tier = uploadTierForRole(role);
    if (tier === 'photo')
        return KIT_PHOTO_ACCEPT;
    if (tier === 'step_reference')
        return STEP_REFERENCE_ACCEPT;
    if (tier === 'manuscript')
        return MANUSCRIPT_ACCEPT;
    if (tier === 'catch_all')
        return CATCH_ALL_ACCEPT;
    if (tier === 'data')
        return DATA_ACCEPT;
    return ARTWORK_ACCEPT;
}
//# sourceMappingURL=upload-file-types.js.map